import type {
  PostImageSurveyInteractiveQuestionRequestBody,
  PostOpenInteractiveQuestionAnswerRequestBody,
  PostSurveyInteractiveQuestionRequestBody,
  PostWidgetInteractiveQuestionRequestBody,
} from '@genially/contracts';
import { InteractiveQuestionType } from '@genially/document-model';
import type { InteractiveQuestionState, PlayerUserAnswer } from '@genially/p2p-lib';
import { isPlayerUserAnswerScoreable } from '@genially/p2p-lib';
import { SidebarType } from '@genially/ui/build/components/SidebarSummary/types/SidebarType';
import { calculateSidebarTypeFromSelectedQuestion } from '@genially/ui/build/components/SidebarSummary/utils/calculateSidebarTypeFromSelectedQuestion';
import { isInteractiveQuestionItemData } from '@genially/ui/build/components/SidebarSummary/utils/isInteractiveQuestion';

import type { PlayPostMessageInteractiveQuestion } from '../../../../../../../shared/domain/types/PlayPostMessageInteractiveQuestion';
import type { AnswerBullet } from './describeUserAnswer';
import { describeUserAnswer } from './describeUserAnswer';
import { latestAnswerPerPlayer, toPercentage } from './sessionAnswers';

/**
 * Which reading a question gets. Not one per question type: a text-categorizable widget
 * reads exactly like a survey, and every autocorrective type reads like a quiz.
 */
export type QuestionAnalyticsKind = 'scorable' | 'survey' | 'rating' | 'openAnswer';

/** The cards above the chart. The panel owns the wording; this owns the arithmetic. */
export type QuestionAnalyticsCardKey =
  | 'responses'
  | 'votes'
  | 'participation'
  | 'hitRate'
  | 'errorRate'
  | 'average'
  | 'topValue';

export type QuestionAnalyticsCard = { key: QuestionAnalyticsCardKey; value: string };

export type QuestionAnalyticsBar = {
  /** What a row's answer is matched against, both for the filter and for the ordering. */
  id: string;
  /** Undefined for image survey options with no caption; the panel numbers those. */
  label: string | undefined;
  /** Only image surveys have one: the option's picture, shown next to the bar. */
  imageUrl: string | undefined;
  optionPosition: number;
  votes: number;
  /** 0..100 over the votes cast, not over the participants: these are votes. */
  percentage: number;
};

export type QuestionOpenAnswer = { username: string; text: string; timestamp: number };

export type QuestionAnalyticsChart =
  | { kind: 'pie'; correct: number; incorrect: number; unanswered: number }
  | { kind: 'bars'; bars: QuestionAnalyticsBar[] }
  | { kind: 'openAnswers'; answers: QuestionOpenAnswer[] };

export type QuestionUserStatus = 'correct' | 'incorrect' | 'answered' | 'unanswered';

export type QuestionAnalyticsUserRow = {
  username: string;
  status: QuestionUserStatus;
  /** The bars this person's answer falls under. Empty when they did not answer. */
  barIds: string[];
  /** Empty means there is nothing to unfold, and the row shows no chevron. */
  details: AnswerBullet[];
};

export type QuestionAnalytics = {
  question: PlayPostMessageInteractiveQuestion;
  kind: QuestionAnalyticsKind;
  /** Random questions are listed under a generic label; their options live in the bank. */
  isRandom: boolean;
  cards: QuestionAnalyticsCard[];
  chart: QuestionAnalyticsChart;
  /** Empty for open answers: the list of texts is already the whole reading. */
  rows: QuestionAnalyticsUserRow[];
};

type DocumentOption = { Id: string; Text?: string; Image?: string };

const documentOptions = (
  question: PlayPostMessageInteractiveQuestion,
): DocumentOption[] =>
  'Answers' in question ? ((question.Answers as DocumentOption[]) ?? []) : [];

const isRandomQuestion = (question: PlayPostMessageInteractiveQuestion) =>
  isInteractiveQuestionItemData(question) &&
  question.Type === InteractiveQuestionType.Random;

const answerIdsOf = ({ answer }: PlayerUserAnswer): string[] => {
  const { answerIds } = answer as
    | PostSurveyInteractiveQuestionRequestBody
    | PostImageSurveyInteractiveQuestionRequestBody;

  return answerIds ?? [];
};

const widgetGroupOf = ({ answer }: PlayerUserAnswer): string | undefined => {
  const { userAnswer } = answer as PostWidgetInteractiveQuestionRequestBody;
  const { score, answerText } = userAnswer ?? {};

  return score !== undefined ? String(score.value) : answerText;
};

/**
 * Which reading applies. A categorizable widget splits in two: a rating scale when every
 * answer carries a numeric score, and a survey otherwise — the same split the individual
 * summary already makes, so the two views can never group the same answers differently.
 */
const calculateKind = (
  question: PlayPostMessageInteractiveQuestion,
  answers: PlayerUserAnswer[],
): QuestionAnalyticsKind => {
  switch (calculateSidebarTypeFromSelectedQuestion(question)) {
    case SidebarType.OpenAnswer:
      return 'openAnswer';
    case SidebarType.Survey:
    case SidebarType.ImageSurvey:
      return 'survey';
    case SidebarType.WidgetCategorizable:
      return answers.length > 0 &&
        answers.every(answer => {
          const { userAnswer } =
            answer.answer as PostWidgetInteractiveQuestionRequestBody;

          return userAnswer?.score !== undefined;
        })
        ? 'rating'
        : 'survey';
    default:
      return 'scorable';
  }
};

const buildBars = (
  entries: { id: string; label: string | undefined; imageUrl?: string }[],
  votesById: Map<string, number>,
): QuestionAnalyticsBar[] => {
  const totalVotes = Array.from(votesById.values()).reduce(
    (total, votes) => total + votes,
    0,
  );

  return entries.map(({ id, label, imageUrl }, index) => {
    const votes = votesById.get(id) ?? 0;

    return {
      id,
      label,
      imageUrl,
      optionPosition: index + 1,
      votes,
      percentage: toPercentage(votes, totalVotes),
    };
  });
};

/**
 * The bars of a survey. Native surveys list every option the author wrote, in document
 * order and including the ones nobody picked, so the host can see that the option they
 * expected got zero. Widgets can only list what came back: their universe of options is
 * not declared anywhere, it is only known through the answers.
 */
const buildSurveyBars = (
  question: PlayPostMessageInteractiveQuestion,
  answers: PlayerUserAnswer[],
  isWidget: boolean,
): QuestionAnalyticsBar[] => {
  const votesById = new Map<string, number>();

  const countVote = (id: string) => votesById.set(id, (votesById.get(id) ?? 0) + 1);

  if (isWidget) {
    answers.forEach(answer => {
      const group = widgetGroupOf(answer);

      if (group !== undefined) {
        countVote(group);
      }
    });

    return buildBars(
      Array.from(votesById.keys())
        .sort((a, b) => a.localeCompare(b))
        .map(id => ({ id, label: id })),
      votesById,
    );
  }

  answers.forEach(answer => answerIdsOf(answer).forEach(countVote));

  return buildBars(
    documentOptions(question).map(({ Id, Text, Image }) => ({
      id: Id,
      label: Text || undefined,
      imageUrl: Image || undefined,
    })),
    votesById,
  );
};

/** The scale a rating widget was answered on: the largest `max` any answer reported. */
const ratingScale = (answers: PlayerUserAnswer[]) =>
  answers.reduce((max, { answer }) => {
    const { userAnswer } = answer as PostWidgetInteractiveQuestionRequestBody;

    return Math.max(max, userAnswer?.score?.max ?? 0);
  }, 0);

/** Every value of the scale, ascending, so a value nobody picked still shows as zero. */
const buildRatingBars = (answers: PlayerUserAnswer[]): QuestionAnalyticsBar[] => {
  const votesById = new Map<string, number>();

  answers.forEach(answer => {
    const group = widgetGroupOf(answer);

    if (group !== undefined) {
      votesById.set(group, (votesById.get(group) ?? 0) + 1);
    }
  });

  const scale = ratingScale(answers);
  const values = Array.from({ length: scale }, (_, index) => String(index + 1));

  return buildBars(
    values.map(id => ({ id, label: id })),
    votesById,
  );
};

const ratingAverage = (answers: PlayerUserAnswer[]) => {
  const values = answers
    .map(({ answer }) => {
      const { userAnswer } = answer as PostWidgetInteractiveQuestionRequestBody;

      return userAnswer?.score?.value;
    })
    .filter((value): value is number => value !== undefined);

  if (values.length === 0) {
    return 0;
  }

  return (
    Math.round((values.reduce((total, value) => total + value, 0) / values.length) * 10) /
    10
  );
};

/** The most chosen value. A tie goes to the lowest value, which is already bar order. */
const topBar = (bars: QuestionAnalyticsBar[]) =>
  bars.reduce<QuestionAnalyticsBar | undefined>(
    (top, bar) => (top === undefined || bar.votes > top.votes ? bar : top),
    undefined,
  );

const STATUS_ORDER: Record<QuestionUserStatus, number> = {
  correct: 0,
  incorrect: 1,
  answered: 1,
  unanswered: 2,
};

/**
 * Correct, then wrong, then whoever said nothing, and inside each group by name. The name
 * as the tie-breaker is what keeps the list still while answers arrive: sorting by the
 * moment someone answered would reshuffle the rows under the host's eyes.
 */
const sortScorableRows = (rows: QuestionAnalyticsUserRow[]) =>
  [...rows].sort(
    (a, b) =>
      STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
      a.username.localeCompare(b.username),
  );

/** Same idea where there is no right answer: grouped by option, then by name. */
const sortOptionRows = (
  rows: QuestionAnalyticsUserRow[],
  bars: QuestionAnalyticsBar[],
) => {
  const positionOf = ({ barIds }: QuestionAnalyticsUserRow) => {
    const index = bars.findIndex(({ id }) => barIds.includes(id));

    return index === -1 ? Number.POSITIVE_INFINITY : index;
  };

  return [...rows].sort(
    (a, b) => positionOf(a) - positionOf(b) || a.username.localeCompare(b.username),
  );
};

/**
 * The analytics read of a single resolved question, derived from the session state this
 * client already holds. No network and no server aggregation, like the session view.
 *
 * Every percentage is read over the participants of the session, never over whoever
 * happened to answer: two correct answers out of two in a room of twenty is a 10% hit
 * rate, not a 100% one.
 */
export const buildQuestionAnalytics = ({
  question,
  state,
  participantUsernames,
  resolveBankAnswerText,
}: {
  question: PlayPostMessageInteractiveQuestion;
  state: InteractiveQuestionState | undefined;
  participantUsernames: string[];
  /** Random questions only: what the drawn bank question calls each of its options. */
  resolveBankAnswerText?: (answerId: string) => string | undefined;
}): QuestionAnalytics => {
  const answers = state ? latestAnswerPerPlayer(state) : [];
  const isRandom = isRandomQuestion(question);
  const kind = calculateKind(question, answers);

  const totalParticipants = participantUsernames.length;
  const respondents = answers.length;
  const participation = toPercentage(respondents, totalParticipants);

  const answerByUsername = new Map(
    answers.map(answer => [answer.player.username, answer] as const),
  );

  const detailsOf = (answer: PlayerUserAnswer | undefined): AnswerBullet[] =>
    answer === undefined
      ? []
      : describeUserAnswer({
          question,
          answer: answer.answer,
          // Only a random question's answers need the bank; everything else names its own
          // options in the document, and handing them a resolver would blank them out.
          resolveBankAnswerText: isRandom ? resolveBankAnswerText : undefined,
        });

  if (kind === 'openAnswer') {
    const openAnswers = answers
      .filter(({ answer }) => !(answer as { isHidden?: boolean }).isHidden)
      .map(({ player, answer }) => ({
        username: player.username,
        text: (answer as PostOpenInteractiveQuestionAnswerRequestBody).answerText ?? '',
        timestamp: answer.timestamp,
      }))
      // Most recent first, so what the room just wrote is on top without scrolling.
      .sort((a, b) => b.timestamp - a.timestamp);

    return {
      question,
      kind,
      isRandom,
      cards: [
        { key: 'responses', value: String(respondents) },
        { key: 'participation', value: `${participation}%` },
      ],
      chart: { kind: 'openAnswers', answers: openAnswers },
      rows: [],
    };
  }

  if (kind === 'scorable') {
    const scoreable = answers.filter(isPlayerUserAnswerScoreable);
    const correct = scoreable.filter(({ isCorrect }) => isCorrect).length;
    const incorrect = scoreable.length - correct;

    const rows = participantUsernames.map<QuestionAnalyticsUserRow>(username => {
      const answer = answerByUsername.get(username);
      const scoreableAnswer =
        answer && isPlayerUserAnswerScoreable(answer) ? answer : undefined;

      return {
        username,
        status: scoreableAnswer
          ? scoreableAnswer.isCorrect
            ? 'correct'
            : 'incorrect'
          : 'unanswered',
        barIds: [],
        details: detailsOf(scoreableAnswer),
      };
    });

    return {
      question,
      kind,
      isRandom,
      cards: [
        { key: 'responses', value: String(scoreable.length) },
        { key: 'participation', value: `${participation}%` },
        { key: 'hitRate', value: `${toPercentage(correct, totalParticipants)}%` },
        { key: 'errorRate', value: `${toPercentage(incorrect, totalParticipants)}%` },
      ],
      chart: {
        kind: 'pie',
        correct,
        incorrect,
        // Never negative: the three slices are meant to add up to the room.
        unanswered: Math.max(totalParticipants - correct - incorrect, 0),
      },
      rows: sortScorableRows(rows),
    };
  }

  const isWidget = !isInteractiveQuestionItemData(question);

  const bars =
    kind === 'rating'
      ? buildRatingBars(answers)
      : buildSurveyBars(question, answers, isWidget);

  const totalVotes = bars.reduce((total, { votes }) => total + votes, 0);

  const barIdsOf = (answer: PlayerUserAnswer | undefined): string[] => {
    if (answer === undefined) {
      return [];
    }

    if (isWidget) {
      const group = widgetGroupOf(answer);

      return group === undefined ? [] : [group];
    }

    return answerIdsOf(answer);
  };

  const rows = participantUsernames.map<QuestionAnalyticsUserRow>(username => {
    const answer = answerByUsername.get(username);

    return {
      username,
      status: answer ? 'answered' : 'unanswered',
      barIds: barIdsOf(answer),
      details: detailsOf(answer),
    };
  });

  const cards: QuestionAnalyticsCard[] = [
    { key: 'votes', value: String(totalVotes) },
    { key: 'participation', value: `${participation}%` },
  ];

  if (kind === 'rating') {
    const scale = ratingScale(answers);
    const top = topBar(bars);

    cards.push(
      { key: 'average', value: `${ratingAverage(answers)} / ${scale}` },
      { key: 'topValue', value: top && top.votes > 0 ? (top.label ?? '') : '–' },
    );
  }

  return {
    question,
    kind,
    isRandom,
    cards,
    chart: { kind: 'bars', bars },
    rows: sortOptionRows(rows, bars),
  };
};
